import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Layout, Modal, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	isAllowedToApproveEntityRequests,
	isInternalTech,
	isUserAllowedToAccess,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { EmptyState } from '../empty_state/EmptyState';
import PermissionDeniedPage from '../permission_denied_page/PermissionDeniedPage';
import OWButton from '../ow_button/OWButton';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS } from '../../actions/entity_add_requests_actions';
import {
	entityAddRequestsRestCrudThunks,
	refreshEntityAddRequestCounts,
} from '../../thunks/entity_add_requests_thunks';
import {
	ENTITY_REQUEST_ENTITY_TYPES,
	REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG,
} from '../../types/entity_add_requests';
import posthog from 'posthog-js';
import {
	REQUESTER_PIPELINE_FILTERS,
	APPROVER_PIPELINE_FILTERS,
} from './entity_add_request_pipeline_filters';
import { getEntityAddRequestColumns } from './entityAddRequestColumns';
import {
	ENTITY_ADD_REQUEST_DEEP_LINK_TARGETS,
	getEntityAddRequestDeepLinkTarget,
	getRequestsToAddSuppliersListPath,
} from './entityAddRequestDeepLink';
import RequestToAddSupplierForm from '../request_to_add_supplier_form/RequestToAddSupplierForm';
import ReviewEntityAddRequestModal from '../review_entity_add_request_modal/ReviewEntityAddRequestModal';
import ViewEntityAddRequestModal from '../review_entity_add_request_modal/ViewEntityAddRequestModal';
import EntityAddRequestAuditsModal from '../entity_add_request_audits_modal/EntityAddRequestAuditsModal';
import EntityRequestFormBuilder from '../entity_request_form/EntityRequestFormBuilder';
import { OWSubTab, OWSubTabs } from '../ow_sub_tabs/OWSubTabs';

const { Content } = Layout;
const TARGET_COLLECTION = 'entityAddRequestsIndex';

// Entity-type strip above the list. Suppliers is the only type the backend can materialize today;
// Assets (both roles) and Parts (internal service teams only) are shown disabled so the roadmap is
// visible without offering a surface that would come back empty.
const SUPPLIERS_SUB_TAB_KEY = 'suppliers';
const SUPPLIERS_SUB_TAB: OWSubTab = { key: SUPPLIERS_SUB_TAB_KEY, label: 'Suppliers' };
const ASSETS_SUB_TAB: OWSubTab = { key: 'assets', label: 'Assets', disabled: true };
const PARTS_SUB_TAB: OWSubTab = { key: 'parts', label: 'Parts', disabled: true };
const BUYER_SUB_TABS: OWSubTab[] = [SUPPLIERS_SUB_TAB, ASSETS_SUB_TAB];
const SUPPLIER_SUB_TABS: OWSubTab[] = [SUPPLIERS_SUB_TAB, ASSETS_SUB_TAB, PARTS_SUB_TAB];

function RequestsToAddSuppliersIndexPage({
	entityAddRequests,
	currentUser,
	companyConfig,
	userType,
	updateFilters,
	clearAndUpdateFilters,
	updateSorters,
	refreshCounts,
	deleteRequest,
	fetchRequest,
	match,
	history,
}) {
	const [refreshToken, setRefreshToken] = useState(0);
	const [requestFormRecord, setRequestFormRecord] = useState(undefined);
	const [requestFormVisible, setRequestFormVisible] = useState(false);
	const [reviewRecord, setReviewRecord] = useState(undefined);
	const [viewRecord, setViewRecord] = useState(undefined);
	const [auditRecord, setAuditRecord] = useState(undefined);
	const [formBuilderVisible, setFormBuilderVisible] = useState(false);

	const canApprove = useMemo(
		() =>
			isAllowedToApproveEntityRequests(
				userType,
				companyConfig,
				nullSafeGetOrElse('roles', currentUser, [])
			),
		[companyConfig, currentUser, userType]
	);

	// Submitting a new-supplier request is gated by the requestNewSuppliers access permission
	// (nested under accessPermissions.others — use the nested reader, not isAllowedToPerform).
	const canRequest = useMemo(
		() =>
			isUserAllowedToAccess(userType)(
				PERMISSION_NAMES.REQUEST_NEW_SUPPLIERS,
				companyConfig,
				nullSafeGetOrElse('roles', currentUser, [])
			),
		[companyConfig, currentUser, userType]
	);

	// On the supplier build this surface belongs to internal service teams only — third-party
	// suppliers have no private network of their own to request additions to.
	const isRoleSupported = useMemo(
		() => userType !== ROLE_TYPES.SUPPLIER || isInternalTech(currentUser),
		[currentUser, userType]
	);

	// The route only checks authorizedRoles, so gate the list itself. Admins auto-pass the permission
	// checks, so also require the PostHog feature flag — otherwise direct navigation would expose the
	// company-wide table + form builder while the feature is disabled.
	const featureEnabled = !!posthog.isFeatureEnabled(REQUEST_TO_ADD_ENTITIES_FEATURE_FLAG);
	const hasAccess = featureEnabled && isRoleSupported && (canApprove || canRequest);

	// The table dispatches this itself, so it takes the thunk creator rather than a bound dispatcher.
	const fetchRequests = useMemo(() => entityAddRequestsRestCrudThunks(userType).read, [userType]);

	const refreshTable = () => setRefreshToken((token) => token + 1);

	const openNewRequest = () => {
		setRequestFormRecord(undefined);
		setRequestFormVisible(true);
	};
	const openEdit = (record) => {
		setRequestFormRecord(record);
		setRequestFormVisible(true);
	};
	const hideRequestForm = () => setRequestFormVisible(false);

	// `/…/suppliers/:id` is where the EntityRequestStatusChange emails' "View Request" button lands.
	// The id is fetched on its own rather than read out of the table, so the link works regardless of
	// which page/filter the list happens to be on.
	const deepLinkId = nullSafeGet('params.id', match);
	const listPath = getRequestsToAddSuppliersListPath(userType);

	// Closing a deep-linked modal drops back to the plain list so a refresh doesn't reopen it.
	const clearDeepLink = useCallback(() => {
		if (deepLinkId) {
			history.replace(listPath);
		}
	}, [deepLinkId, history, listPath]);

	const openReview = (record) => setReviewRecord(record);
	const hideReview = () => {
		setReviewRecord(undefined);
		clearDeepLink();
	};

	const openView = (record) => setViewRecord(record);
	const hideView = () => {
		setViewRecord(undefined);
		clearDeepLink();
	};

	const currentUserEmail = nullSafeGet('email', currentUser);

	// `companyConfig` is dispatched without being awaited during session init and `ProtectedRoute`
	// only gates on roles, so this page can render before permissions resolve — `hasAccess` and
	// `canApprove` both start false and flip once the config lands. The effect therefore has to
	// re-run on them, or an email link opened on a cold session would bail out once and never
	// reopen. The ref keeps that to a single fetch per id so a later re-run can't reopen a modal
	// the user has already closed. `fetchRequest` is deliberately not a dependency: connect rebuilds
	// it on every render, which would turn this into a refetch loop.
	const handledDeepLinkIdRef = useRef(undefined);

	// No in-flight cancellation flag here on purpose. Closing a deep-linked modal replaces the URL
	// with the sibling list route, which unmounts this instance outright, so there is no live
	// component left for a late response to reopen. A flag would only break StrictMode's
	// setup/cleanup/setup in development: the first run claims the ref and starts the fetch, the
	// second bails on the ref, and a cancelled first response would leave the modal shut.
	useEffect(() => {
		if (!deepLinkId || !hasAccess || handledDeepLinkIdRef.current === deepLinkId) {
			return;
		}
		handledDeepLinkIdRef.current = deepLinkId;
		fetchRequest(deepLinkId)
			.then((record) => {
				if (
					getEntityAddRequestDeepLinkTarget(record, canApprove, currentUserEmail) ===
					ENTITY_ADD_REQUEST_DEEP_LINK_TARGETS.review
				) {
					setReviewRecord(record);
				} else {
					setViewRecord(record);
				}
			})
			.catch((error) => {
				message.error(error || 'We could not open that request.');
				history.replace(listPath);
			});
	}, [deepLinkId, hasAccess, canApprove, currentUserEmail]);

	const openAudits = (record) => setAuditRecord(record);
	const hideAudits = () => setAuditRecord(undefined);

	const openFormBuilder = () => setFormBuilderVisible(true);
	const hideFormBuilder = () => setFormBuilderVisible(false);

	// An approved request has a created supplier behind it, so View goes straight to that supplier's
	// page in a new tab — the request itself has nothing left to decide. Requests approved without a
	// created entity fall back to the read-only request detail, which is also what the email deep link
	// opens for an approved request.
	const handleView = (record) => {
		const createdEntityId = nullSafeGet('createdEntityId', record);
		if (createdEntityId) {
			window.open(`/${userType}/suppliers/detail/${createdEntityId}`, '_blank', 'noreferrer');
			return;
		}
		openView(record);
	};

	const handleDelete = (record) => {
		Modal.confirm({
			title: 'Delete this request?',
			content: 'This request will be permanently removed.',
			okText: 'Delete',
			okType: 'danger',
			onOk: () => deleteRequest(record).then(refreshTable),
		});
	};

	const handleActionSuccess = () => {
		refreshTable();
	};

	const columns = useMemo(
		() =>
			getEntityAddRequestColumns({
				canApprove,
				currentUserEmail,
				onReview: openReview,
				onEdit: openEdit,
				onDelete: handleDelete,
				onView: handleView,
				onShowAudits: openAudits,
			}),
		[canApprove, currentUserEmail]
	);

	// The list is company-wide for everyone; ownership only gates the edit/delete row actions.
	const preAppliedFilters = useMemo(
		() => ({ entityType: ENTITY_REQUEST_ENTITY_TYPES.supplier }),
		[]
	);

	const initialFilters = {};

	// Parts requests only exist for internal service teams, who are the only supplier-side users that
	// reach this page at all (see isRoleSupported).
	const subTabs = userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_SUB_TABS : BUYER_SUB_TABS;

	// Suppliers is the only enabled tab, and OWSubTabs swallows clicks on disabled tabs, so the only
	// key that can arrive here is the one already selected. Routing lands here once Assets/Parts
	// requests are supported.
	const ignoreSubTabChange = (selectedKey: string) => {};

	// `inline` keeps the strip beside the search box with their baselines aligned; the extra 8px on top
	// of the header's own gap leaves the 16px the private network header uses between the two.
	const leftActions = (
		<OWSubTabs
			inline
			className="mr-2"
			tabs={subTabs}
			activeKey={SUPPLIERS_SUB_TAB_KEY}
			onChange={ignoreSubTabChange}
		/>
	);

	const rightActions = canApprove ? (
		<OWButton icon={<EditOutlined />} onClick={openFormBuilder}>
			Edit Request Form
		</OWButton>
	) : canRequest ? (
		<OWButton type="primary" size="large" onClick={openNewRequest}>
			+ New Request
		</OWButton>
	) : null;

	const emptyState = (
		<EmptyState
			graphic={
				<img
					alt=""
					style={{ marginBottom: 8 }}
					src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
				/>
			}
			headline={'No suggested entities yet.'}
			body={
				<div style={{ textAlign: 'center', maxWidth: 440 }}>
					{canApprove
						? 'There are no supplier requests to review right now.'
						: 'Request a new supplier to get started.'}
				</div>
			}
		/>
	);

	if (!hasAccess) {
		return <PermissionDeniedPage />;
	}

	return (
		<Content className="requestsToAddPage" style={{ padding: '0 0.5em' }}>
			{requestFormVisible ? (
				<RequestToAddSupplierForm
					visible={requestFormVisible}
					record={requestFormRecord}
					hideModal={hideRequestForm}
					onSuccess={handleActionSuccess}
				/>
			) : null}
			{reviewRecord ? (
				<ReviewEntityAddRequestModal
					visible={!!reviewRecord}
					record={reviewRecord}
					hideModal={hideReview}
					onSuccess={handleActionSuccess}
				/>
			) : null}
			{viewRecord ? (
				<ViewEntityAddRequestModal
					visible={!!viewRecord}
					record={viewRecord}
					hideModal={hideView}
				/>
			) : null}
			{auditRecord ? (
				<EntityAddRequestAuditsModal
					visible={!!auditRecord}
					record={auditRecord}
					hideModal={hideAudits}
				/>
			) : null}
			{formBuilderVisible ? (
				<EntityRequestFormBuilder
					visible={formBuilderVisible}
					hideModal={hideFormBuilder}
					onSuccess={handleActionSuccess}
				/>
			) : null}
			<PaginatedReduxTableWithHeader
				key={refreshToken}
				targetCollectionName={TARGET_COLLECTION}
				showHeader
				updateFilters={updateFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				updateSorters={updateSorters}
				refreshCounts={refreshCounts}
				stateSlice={entityAddRequests}
				pipeLineFilters={canApprove ? APPROVER_PIPELINE_FILTERS : REQUESTER_PIPELINE_FILTERS}
				filterConfig={[]}
				entityCollectionName="entity_add_requests"
				tableColumns={columns}
				fetchData={fetchRequests}
				initialPagination={{ current: 1, pageSize: 10 }}
				initialSorters={{ sort_by: 'id', order: 'descend' }}
				initialFilters={initialFilters}
				preAppliedFilters={preAppliedFilters}
				leftActions={leftActions}
				rightActions={rightActions}
				emptyState={emptyState}
			/>
		</Content>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	entityAddRequests: state.entity_add_requests,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollection) =>
		dispatch(
			ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollection)
		),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(
			ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)
		),
	refreshCounts: (filters) => dispatch(refreshEntityAddRequestCounts(ownProps.userType)(filters)),
	deleteRequest: (record) =>
		dispatch(entityAddRequestsRestCrudThunks(ownProps.userType).delete(record)),
	fetchRequest: (id) => dispatch(entityAddRequestsRestCrudThunks(ownProps.userType).readOne(id)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(RequestsToAddSuppliersIndexPage))
);
